/**
 * Every treasury order the desk can see, and the drill-down for one.
 *
 * Two components in one file because they are one screen: the list exists to get you to the
 * detail, and the detail is the only place the bridge is visible at all.
 *
 * The detail deliberately shows everything — every leg, every vendor reference, the raw
 * payload. That asymmetry is the point of the whole module: the person who can unstick an
 * order is told what actually happened, and the customer, who cannot, is never shown the
 * house's plumbing. `serialize.ts` on the API side enforces the other half of that wall.
 */
import { Link, useParams } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext.js';
import type { AdminBridgeLeg, AdminTreasuryOrder } from '../contracts/types.js';
import { Badge, Card, EmptyState, ErrorBox, LoadingState } from '../components/ui.js';
import { useAsync } from '../hooks/useAsync.js';
import { formatMoney } from '../lib/money.js';
import { formatDateTime, relativeTime } from '../lib/format.js';

export function TreasuryOrdersPage(): JSX.Element {
  const { api } = useAuth();
  const state = useAsync(() => api.listTreasuryOrders(), []);

  if (state.loading) return <LoadingState label="Reading orders" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;

  const orders = state.data?.orders ?? [];

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Treasury orders</h1>
        <p className="page-head__sub">
          Newest first. Quotes nobody accepted are not orders and are not listed.
        </p>
      </div>

      <Card>
        {orders.length === 0 ? (
          <EmptyState
            glyph="–"
            title="No orders yet"
            hint="A buy or a sell placed from the app appears here."
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Side</th>
                  <th>Customer</th>
                  <th className="num">Naira</th>
                  <th className="num">Coin</th>
                  <th>Status</th>
                  <th>Opened</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <Link to={`/treasury/orders/${o.id}`}>{o.reference}</Link>
                    </td>
                    <td>
                      <Badge tone={o.side === 'buy' ? 'blue' : 'violet'}>{o.side}</Badge>
                    </td>
                    <td>
                      {o.customer.displayName}
                      <br />
                      <span className="muted">{o.customer.reference}</span>
                    </td>
                    <td className="num">{formatMoney(o.ngnAmount)}</td>
                    <td className="num">{formatMoney(o.coinAmount)}</td>
                    <td>
                      <StatusBadge order={o} />
                    </td>
                    <td>{relativeTime(o.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

export function TreasuryOrderDetailPage(): JSX.Element {
  const { api } = useAuth();
  const { id = '' } = useParams<{ id: string }>();
  const state = useAsync(() => api.getTreasuryOrder(id), [id]);

  if (state.loading) return <LoadingState label="Reading the order" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;
  if (state.data === null) return <ErrorBox message="No such order." />;

  const o = state.data;

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">{o.reference}</h1>
        <p className="page-head__sub">
          <Link to="/treasury/orders">All orders</Link> · opened {formatDateTime(o.createdAt)}
        </p>
      </div>

      {o.reviewReason !== null && (
        <div className="state-banner" role="status">
          <div>
            <strong>Held for review:</strong> {o.reviewReason.replace(/_/g, ' ')}. Decide it on
            the <Link to="/treasury/review">review queue</Link>.
          </div>
        </div>
      )}

      <Card title="The trade">
        <div className="table-wrap">
          <table className="table">
            <tbody>
              <Row label="Side">
                <Badge tone={o.side === 'buy' ? 'blue' : 'violet'}>{o.side}</Badge>
              </Row>
              <Row label="Status">
                <StatusBadge order={o} />
              </Row>
              <Row label="Customer">
                {o.customer.displayName} · {o.customer.reference}
                {o.customer.phone !== null && <> · {o.customer.phone}</>}
                {o.customer.email !== null && <> · {o.customer.email}</>}
              </Row>
              <Row label="Naira">{formatMoney(o.ngnAmount)}</Row>
              <Row label="Coin">{formatMoney(o.coinAmount)}</Row>
              <Row label="Through the bridge">{formatMoney(o.usdtAmount)}</Row>
              <Row label="Fee">
                {formatMoney(o.fee)} <span className="muted">({o.feeBps} bps)</span>
              </Row>
              <Row label="Spread">{o.spreadBps} bps</Row>
              <Row label="Settlement">{o.fiatMode}</Row>
              {o.failureReason !== null && <Row label="Failure">{o.failureReason}</Row>}
              <Row label="Completed">
                {o.completedAt === null ? (
                  <span className="muted">not yet</span>
                ) : (
                  formatDateTime(o.completedAt)
                )}
              </Row>
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Bridge legs">
        {o.legs.length === 0 ? (
          <p className="muted">No leg has run yet.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Leg</th>
                  <th className="num">From</th>
                  <th className="num">To</th>
                  <th>Status</th>
                  <th>Vendor</th>
                </tr>
              </thead>
              <tbody>
                {o.legs.map((leg: AdminBridgeLeg) => (
                  <tr key={`${leg.position}-${leg.leg}`}>
                    <td>{leg.position}</td>
                    <td>{leg.leg.replace(/_/g, ' ')}</td>
                    <td className="num">{formatMoney(leg.from)}</td>
                    <td className="num">{formatMoney(leg.to)}</td>
                    <td>
                      <Badge tone={leg.status === 'completed' ? 'green' : leg.status === 'failed' ? 'red' : 'amber'}>
                        {leg.status}
                      </Badge>
                    </td>
                    <td>
                      {leg.provider}
                      {leg.providerReference !== null && (
                        <>
                          <br />
                          <span className="muted">{leg.providerReference}</span>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {o.externalFees.length > 0 && (
        <Card title="What the vendors charged us">
          <p className="muted" style={{ marginTop: 0 }}>
            The house's own cost on this order, as opposed to what the customer was charged.
          </p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Kind</th>
                  <th>Vendor</th>
                  <th className="num">Amount</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {o.externalFees.map((fee, i) => (
                  <tr key={`${fee.kind}-${i}`}>
                    <td>{fee.kind}</td>
                    <td>{fee.provider}</td>
                    <td className="num">{formatMoney(fee.amount)}</td>
                    <td>{fee.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <tr>
      <th style={{ width: '30%' }}>{label}</th>
      <td>{children}</td>
    </tr>
  );
}

/**
 * The house status, with the customer's word for it underneath.
 *
 * Both are shown because they answer different questions: the operator needs the state the
 * machine is actually in, and also needs to know what the customer is being told, so a support
 * call does not turn into two people describing the same order in different vocabularies.
 */
function StatusBadge({ order }: { order: AdminTreasuryOrder }): JSX.Element {
  const tone =
    order.status === 'completed'
      ? 'green'
      : order.status.includes('failed') || order.status.includes('expired')
        ? 'red'
        : order.reviewReason !== null
          ? 'amber'
          : 'blue';
  return (
    <>
      <Badge tone={tone}>{order.status.replace(/_/g, ' ')}</Badge>
      <br />
      <span className="muted">customer sees: {order.publicStatus.replace(/_/g, ' ')}</span>
    </>
  );
}
